import React, { useState } from "react";
import styled from "styled-components";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { MessageSquareHeart, Sparkles, Star } from "lucide-react";
import { useLocation } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { trackProductEvent } from "../lib/analytics";

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(840px at 12% 2%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(920px at 88% 8%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Shell = styled.div`
  padding: 30px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: 40px;
  line-height: 1.08;
  font-weight: 1000;
`;

const Sub = styled.p`
  margin: 14px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.75;
  max-width: 720px;
`;

const Form = styled.div`
  margin-top: 28px;
  display: grid;
  gap: 18px;
`;

const Section = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Label = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const Hint = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
`;

const Stars = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StarBtn = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(245, 158, 11, 0.32)" : "rgba(148, 163, 184, 0.14)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(56, 189, 248, 0.12))"
      : "rgba(2, 6, 23, 0.5)"};
  color: ${({ $active }) => ($active ? "#fde68a" : "#94a3b8")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Select = styled.select`
  width: 100%;
  margin-top: 14px;
  height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  padding: 0 14px;
  background: rgba(2, 6, 23, 0.52);
  color: #f8fafc;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  margin-top: 14px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  padding: 14px;
  background: rgba(2, 6, 23, 0.52);
  color: #f8fafc;
  resize: vertical;
  line-height: 1.7;

  &::placeholder {
    color: #64748b;
  }
`;

const CTA = styled.button`
  height: 52px;
  padding: 0 18px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(90deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
`;

const Status = styled.div`
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #cfefff;
  font-size: 13px;
  line-height: 1.6;
`;

const categories = [
  { value: "overall", label: "Overall experience" },
  { value: "roadmap", label: "Roadmap" },
  { value: "ai_mentor", label: "AI mentor" },
  { value: "dashboard", label: "Dashboard" },
  { value: "community", label: "Community" },
  { value: "pricing", label: "Pricing" },
  { value: "reminders", label: "Reminders" },
];

export default function FeedbackPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("overall");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  const submitFeedback = async () => {
    if (!rating || submitting) return;

    try {
      setSubmitting(true);
      setStatus("");

      await addDoc(collection(db, "product_feedback"), {
        uid: user?.uid || null,
        userEmail: user?.email || null,
        rating,
        category,
        message: message.trim() || null,
        pagePath: location.pathname,
        createdAt: serverTimestamp(),
      });

      void trackProductEvent(
        "product_feedback_submitted",
        {
          rating,
          category,
          hasMessage: !!message.trim(),
          pagePath: location.pathname,
        },
        { user }
      );

      setStatus("Thanks. Your feedback is saved and will help improve skilll.dev.");
      setRating(0);
      setCategory("overall");
      setMessage("");
    } catch (error) {
      setStatus("Could not save feedback right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Wrap>
        <Shell>
          <Eyebrow>
            <Sparkles size={14} />
            Product Feedback
          </Eyebrow>
          <Title>Rate skilll.dev and help us improve</Title>
          <Sub>
            Share what feels useful, what feels confusing, and what should become better next.
          </Sub>

          <Form>
            <Section>
              <Label>How would you rate skilll.dev right now?</Label>
              <Hint>Pick a quick score from 1 to 5 stars.</Hint>
              <Stars>
                {[1, 2, 3, 4, 5].map((value) => (
                  <StarBtn
                    key={value}
                    type="button"
                    $active={rating >= value}
                    onClick={() => setRating(value)}
                  >
                    <Star size={22} fill={rating >= value ? "currentColor" : "none"} />
                  </StarBtn>
                ))}
              </Stars>
            </Section>

            <Section>
              <Label>Which part are you rating?</Label>
              <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Section>

            <Section>
              <Label>Anything specific we should improve?</Label>
              <Hint>Optional, but very helpful if you mention the exact issue or idea.</Hint>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what feels good, what feels weak, or what you want next."
              />
            </Section>

            {status ? (
              <Status>
                <MessageSquareHeart size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
                {status}
              </Status>
            ) : null}

            <CTA onClick={submitFeedback} disabled={submitting || !rating}>
              {submitting ? "Sending feedback..." : "Submit Feedback"}
            </CTA>
          </Form>
        </Shell>
      </Wrap>
    </Page>
  );
}
